import {
  collection,
  doc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { DEFAULT_SUMMARY_TIME, STARTER_CATEGORIES } from '@/config'
import type { HouseholdDoc, Settings } from '@/types'

export function defaultSettings(): Settings {
  return {
    shownCalendars: {},
    calendarOwners: {},
    defaultVisibility: 'private',
    defaultAssignee: 'either',
    askBeforeSharing: true,
    theme: 'system',
    notifications: { reminders: true, summary: true, partner: true },
    summaryTime: DEFAULT_SUMMARY_TIME,
  }
}

/** The household whose allowlist holds this email, or null. */
export async function findHouseholdByEmail(email: string): Promise<string | null> {
  const q = query(collection(db, 'households'), where('memberEmails', 'array-contains', email.toLowerCase()), limit(1))
  const snap = await getDocs(q)
  return snap.empty ? null : snap.docs[0].id
}

/**
 * Joining a household that lists you: the second person, found on the allowlist, or the first, once
 * startHousehold has made it. Writes their own member entry and their householdId.
 */
export async function joinHousehold(hid: string, uid: string, displayName: string): Promise<void> {
  const batch = writeBatch(db)
  batch.update(doc(db, 'households', hid), {
    [`members.${uid}`]: { name: displayName.trim() || 'Me' },
    updatedAt: serverTimestamp(),
    updatedBy: uid,
  })
  batch.set(
    doc(db, 'users', uid),
    {
      householdId: hid,
      settings: defaultSettings(),
      lastSummaryDate: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  )
  await batch.commit()
}

export async function setMemberName(hid: string, uid: string, name: string): Promise<void> {
  await updateDoc(doc(db, 'households', hid), {
    [`members.${uid}`]: { name: name.trim() },
    updatedAt: serverTimestamp(),
    updatedBy: uid,
  })
}

/** Owner-only edits: the household name and the other person's email. */
export async function updateHousehold(
  hid: string,
  uid: string,
  current: HouseholdDoc,
  change: { name?: string; otherEmail?: string },
): Promise<void> {
  const patch: Record<string, unknown> = { updatedAt: serverTimestamp(), updatedBy: uid }
  if (change.name !== undefined) patch.name = change.name.trim()
  if (change.otherEmail !== undefined) {
    const mine = current.memberEmails.find((e) => e !== change.otherEmail?.toLowerCase()) ?? current.memberEmails[0]
    const own = current.memberEmails.length === 1 ? current.memberEmails[0] : mine
    const other = change.otherEmail.trim().toLowerCase()
    patch.memberEmails = other && other !== own ? [own, other] : [own]
  }
  await updateDoc(doc(db, 'households', hid), patch)
}

/** The six starter categories, created only when tapped at first run. */
export async function createStarterCategories(hid: string, uid: string): Promise<void> {
  const batch = writeBatch(db)
  STARTER_CATEGORIES.forEach((c, i) => {
    batch.set(doc(collection(db, 'households', hid, 'categories')), {
      name: c.name,
      color: c.color,
      order: i + 1,
      archived: false,
      createdBy: uid,
      updatedBy: uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
  })
  await batch.commit()
}
